import _, { isEmpty, isFunction } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { v4 as uuid, validate } from 'uuid';

import {
  selectRelatedReportModel,
  selectRelatedDuplicataModel,
  selectRelatedMultaModel,
  selectRelatedEmissaoIcmsModel,
  selectDeclaracoesImportacao,
  selectRelatedCalculoModel,
  selectMode,
} from '../../../../../features/declaracao-importacao/declaracaoImportacaoSelectors';
import { clienteActions } from '../../../../../features/cliente/clienteSlice';
import { IDLE_CONTROL } from '../../../../../features/config-control';
import {
  changeToConsultMode,
  setRelatedReportModel,
  resetRelatedReportModel,
  setRelatedCalculoModel,
  setRelatedRegistroModel,
  setRelatedDuplicataModel,
  setRelatedMultaModel,
  resetRelatedMultaModel,
  failure,
  loading,
  success,
  resetRelatedEmissaoIcmsModel,
  setRelatedEmissaoIcmsModel,
  loadingDI,
  resetMode,
  updateOnList,
  changeLastHistoryModel,
} from '../../../../../features/declaracao-importacao/declaracaoImportacaoSlice';
import { ReportUtils } from '../../../../../utils/general/report/reportUtils';
import {
  AWAITING_STATUS,
  CLOSED_STATUS,
  DONE_STATUS,
  FAIL_STATUS,
  isAwaitingStatus,
  isOpenStatus,
  OPEN_STATUS,
  SUBMITTING_STATUS,
} from '../../../../../utils/hooks/form/dialog/formDialogUtils';
import {
  activateByIdFromFaturaAsync,
  atualizaDadosCargaAsync,
  calcularDisLoteAsync,
  conferirByIdFromFaturaAsync,
  diagnosticoDiByIdAsync,
  duplicarByIdFromFaturaAsync,
  fetchAllDecImportWithPaginationAsync,
  fetchCapaDiDuimpByIdFromFaturaAsync,
  generateReportDIDiagnosticoAsync,
  generateReportDIRegistroAsync,
  inactivateByIdFromFaturaAsync,
  liberarByIdFromFaturaAsync,
  obterChavesDuimpAsync,
  reabrirByIdAsync,
  registrarDuimpByIdAsync,
  retransmitirByIdFromFaturaAsync,
  transmissaoDiagnosticoDuimpByIdAsync,
  transmissaoDuimpByIdAsync,
  retificarDuimpAsync,
  recuperarStatusDuimpAsync,
  apagarTransmissaoDuimpAsync,
} from '../../../../../features/declaracao-importacao/declaracaoImportacaoThunks';
import useFormDialogSync from '../../../../../utils/hooks/form/dialog/useFormDialogSync';
import { declaracaoImportacaoAPI } from '../../../../../features/declaracao-importacao/declaracaoImportacaoAPI';
import useGenericDownloader from '../../../../../utils/hooks/downloader/useGenericDownloader';
import { setErrorFeedback, setSuccessFeedback } from '../../../../../features/feedback/feedbackSlice';
import { useHasPermission } from '../../../../../ts/common/hooks/useHasPermission/useHasPermission';
import { GERARXML, RELATORIOS, TRANSMISSAO } from '../../../../../ts/common/hooks/useHasPermission/permissionsEnum';
import useConfigurePopUp from '../../../../../ts/common/hooks/popUps/useConfigurePopUp';
import { fetchDataDiagnostico } from '../../../../../ts/common/services/declaracaoImportacaoApi';
import useOperationMiddleware from '../../../../../utils/hooks/operation/middleware/useOperationMiddleware';
import { TIPO_DI_DUIMP } from '../../../../../utils/general/fatura/faturaUtils';
import { followUpActions } from '../../../../../features/follow-up/followUpSlice';
import { TipoProdutoUtils } from '../../../../../shared-components/select-tipo-produto/tipoProdutoUtils';
import { followUpAPI } from '../../../../../features/follow-up/followUpAPI';
import {
  formatDate,
  normalizeData,
  normalizeNumeral,
  unnormalizeNumeral,
  isValid,
  normalizeYear,
} from '../../../../../utils/general/general-utils';
import { isNaoCalculadaStatus } from '../../../../../utils/general/operations/operationUtils';
import { faturaAPI } from '../../../../../features/fatura/faturaAPI';
import { DECLARACAO_IMPORTACAO_ENDPOINT } from '../../../../../utils/api/domain/domain-endpoint-utils';
import { isCalculateMode } from '../../../../../utils/store/store-utils';

import {
  formatBrazilianNumericDecimal,
  formatLINumber,
  formatNotZero,
} from '../../../../../utils/hooks/form/field/formatters';
import { doPutQCXRequest } from '../../../../../utils/api/domain/domain-api-utils';
import useCurrentUnidadeNegocio from '../../../../../utils/hooks/user/useCurrentUnidadeNegocio';

const useDeclaracaoImportacaoActionsMenu = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const location = useLocation();

  const needsRefetch = location?.pathname?.includes('detalhes');

  const unidadeNegocioSelecionada = useCurrentUnidadeNegocio();

  const [exibirModalTransmissao, setExibirModalTransmissao] = useState(false);
  const [dataDiagnostico, setDataDiagnostico] = useState(null);
  const [processoDiagnostico, setProcessoDiagnostico] = useState(null);
  const [idDiToTransmissao, setIdDiToTransmissao] = useState(null);
  const [isDuimpSelected, setIsDuimpSelected] = useState(null);
  const [faturaSelected, setFaturaSelected] = useState(null);
  const showButtonTransmitirSemTributos = useMemo(() => {
    const diagnosticos = dataDiagnostico?.diagnostico ?? [];
    const listaDiagnosticos = dataDiagnostico?.listaDiagnostico ?? [];
    const todosDiagnosticos = diagnosticos.concat(listaDiagnosticos);
    const listasErros = todosDiagnosticos.map((d) => d?.erros ?? []);
    const mensagensErros = listasErros.map((erros) => erros.map((e) => e?.mensagem ?? '')).join('. ');

    return mensagensErros.includes('Para codigoAgregacao/idRegime/idFundamentoLegal');
  }, [dataDiagnostico]);

  const [exibirModalTransmissaoDuimp, setExibirModalTransmissaoDuimp] = useState(false);
  const [modelDuimpSelecionada, setModelDuimpSelecionada] = useState({});
  const [exibirModalRelatorioCalculoDi, setExibirModalRelatorioCalculoDi] = useState(false);

  const [idDiSelecionada, setIdDiSelecionada] = useState(null);
  const [idFaturaSelecionada, setIdFaturaSelecionada] = useState(null);

  const canGenerateDIDiag = useHasPermission(GERARXML.DIAGNOSTICODI);
  const canGenerateTransmissaoDiagnosticoDI = useHasPermission(TRANSMISSAO.DIAGNOSTICODI);

  const canGenerateDIReg = useHasPermission(GERARXML.REGISTRODI);
  const canGenerateTransmissaoRegistroDI = useHasPermission(TRANSMISSAO.REGISTRODI);

  const canGenerateRelatorioSap = useHasPermission(RELATORIOS.SAIDA_SAP_VISUALIZAR);

  const [exibirModalDespachante, setExibirModalDespachante] = useState(false);
  const [duimpSelecionadaTransmissao, setDuimpSelecionadaTransmissao] = useState(null);

  const genericDownloader = useGenericDownloader({
    autoDownload: true,
  });

  const calculoRelatedModel = useSelector(selectRelatedCalculoModel);
  const declaracoesImportacao = useSelector(selectDeclaracoesImportacao);
  const reportRelatedModel = useSelector(selectRelatedReportModel);
  const duplicataRelatedModel = useSelector(selectRelatedDuplicataModel);
  const multaRelatedModel = useSelector(selectRelatedMultaModel);
  const emissaoIcmsRelatedModel = useSelector(selectRelatedEmissaoIcmsModel);

  const mode = useSelector(selectMode);
  const isCalculate = useMemo(() => isCalculateMode(mode), [mode]);

  const [isCalcularAcrescimosDeducoesAlertOpen, setIsCalcularAcrescimosDeducoesAlertOpen] = useState(false);

  const fetchFaturaDiDuimpCapaByFaturaId = useCallback((faturaId) => {
    dispatch(fetchCapaDiDuimpByIdFromFaturaAsync(faturaId));
  }, []);

  const configureDependencies = useCallback(() => {
    dispatch(clienteActions.changeControlTo(IDLE_CONTROL));
  }, []);

  const [isNovoRelatorio, setIsNovoRelatorio] = useState(false);

  const handleCloseAlertAcrescimosDeducoes = () => setIsCalcularAcrescimosDeducoesAlertOpen(false);

  const [handleImprimirClick, formDialogImpressaoStatus, handleFormDialogImpressaoStatus] = useFormDialogSync(
    async (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(
          setRelatedReportModel({
            ...data,
            tipo: ReportUtils.DRAFT_DI_DUIMP,
          })
        );
      }
    },
    []
  );

  const isFormDialogImpressaoOpen = useMemo(() => isOpenStatus(formDialogImpressaoStatus), [formDialogImpressaoStatus]);

  const formDialogImpressaoSubmittingFeedback = useMemo(
    () =>
      t('com.muralis.qcx.gerandoRelatorioDIDUIMP', {
        processo: reportRelatedModel?.declaracaoImportacao?.processo || '#######',
      }),
    [reportRelatedModel]
  );

  const formDialogImpressaoContent = useMemo(
    () =>
      t('com.muralis.qcx.selecioneAbaixoTipoRelatorioGerarProcesso', {
        processo: reportRelatedModel?.declaracaoImportacao?.processo || '#######',
      }),
    [isFormDialogImpressaoOpen, reportRelatedModel]
  );

  const handleCloseFormDialogImpressao = useCallback(
    () => handleFormDialogImpressaoStatus(CLOSED_STATUS),
    [handleFormDialogImpressaoStatus]
  );

  const handleImprimirByIdSubmit = useCallback(
    (values) => {
      handleFormDialogImpressaoStatus(SUBMITTING_STATUS);

      const getTipoRelatorioLabel = (tipo) => {
        if (ReportUtils.isCalculoPorAdicao(tipo)) {
          return t('com.muralis.qcx.calculoAdicaoDIDUIMP');
        }
        if (ReportUtils.isDraftDiDuimp(tipo)) {
          return t('com.muralis.qcx.DIDUIMP.draftDeDIDUIMP');
        }
        if (ReportUtils.isItensPorOrdemDeDigitacao(tipo)) {
          return t('com.muralis.qcx.itensPorOrdemDigitacao');
        }
        if (ReportUtils.isResumoItens(tipo)) {
          return 'Resumo de Itens';
        }
        if (ReportUtils.isSaidaSap(tipo)) {
          return 'Relatório Saída SAP';
        }
        return '';
      };

      const tipoRelatorioLabel = getTipoRelatorioLabel(values?.tipo);

      const fetcher = async () => {
        if (ReportUtils.isDraftDiDuimp(values?.tipo)) {
          return declaracaoImportacaoAPI.generateReportById(values?.declaracaoImportacao?.id);
        }

        if (ReportUtils.isCalculoPorAdicao(values?.tipo)) {
          return declaracaoImportacaoAPI.generateReportAdicoesById(values?.declaracaoImportacao?.id);
        }

        if (ReportUtils.isItensPorOrdemDeDigitacao(values?.tipo)) {
          return declaracaoImportacaoAPI.generateReportItensById(values?.declaracaoImportacao?.id);
        }

        if (ReportUtils.isResumoItens(values?.tipo)) {
          return declaracaoImportacaoAPI.generateReportResumoItensById(values?.declaracaoImportacao?.id);
        }

        if (ReportUtils.isSaidaSap(values?.tipo)) {
          return declaracaoImportacaoAPI.generateReportSaidaSapById(values?.declaracaoImportacao?.id);
        }

        return undefined;
      };

      const handlers = {
        onSuccess: () => {
          dispatch(resetRelatedReportModel());
          handleFormDialogImpressaoStatus(DONE_STATUS);
          const successMessage = t('com.muralis.qcx.mensagem.relatorioTipoGerado', {
            tipoRelatorio: tipoRelatorioLabel,
            processo: values?.declaracaoImportacao?.processo,
          });

          dispatch(
            setSuccessFeedback({
              message: successMessage,
            })
          );
        },
        onNoContent: () => {
          handleFormDialogImpressaoStatus(OPEN_STATUS);

          const infoMessage = t('com.muralis.qcx.mensagem.naoFoiPossivelGerarRelatorio', {
            tipoRelatorio: tipoRelatorioLabel,
            processo: values?.declaracaoImportacao?.processo,
          });

          dispatch(
            setErrorFeedback({
              message: infoMessage,
            })
          );
        },
        onError: async (reason) => {
          console.log(reason);
          handleFormDialogImpressaoStatus(FAIL_STATUS);

          let message = 'Erro desconhecido';
          try {
            if (reason[0]?.response?.data instanceof ArrayBuffer) {
              const decoder = new TextDecoder('utf-8');
              const text = decoder.decode(reason[0].response.data);
              const json = JSON.parse(text);
              message = json.message || message;
            } else {
              message = reason[0]?.response?.data?.message || 'Erro desconhecido';
            }
            if (message.startsWith('Erro ao gerar xml: ')) {
              message = message.replace('Erro ao gerar xml: ', '');
            }
          } catch (error) {
            console.error('Erro ao processar a mensagem de erro:', error);
          }

          const errorMessage = t('com.muralis.qcx.erro.erroGerarRelatorio', {
            tipoRelatorio: tipoRelatorioLabel,
            processo: values?.declaracaoImportacao?.processo,
            mensagem: message,
          });

          dispatch(
            setErrorFeedback({
              message: errorMessage,
            })
          );
        },
      };

      genericDownloader.downloadFileById(fetcher, handlers);
    },
    [handleFormDialogImpressaoStatus]
  );

  const handleConsultarClick = useCallback(
    (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        configureDependencies();

        dispatch(changeToConsultMode());

        history.push(t('com.muralis.qcx.url.importacaoDeclaracoesImportacaoDetalhes', { id: data?.id }));
      }
    },
    [configureDependencies, history]
  );

  const handleGerarXMLDiagnosticoById = useCallback((event, values) => {
    dispatch(
      generateReportDIDiagnosticoAsync({
        id: values?.declaracaoImportacao?.id,
        processo: values?.declaracaoImportacao?.processo,
      })
    );
  }, []);

  const handleGerarXMLRegistroById = useCallback((event, values) => {
    dispatch(
      generateReportDIRegistroAsync({
        id: values?.declaracaoImportacao?.id,
        processo: values?.declaracaoImportacao?.processo,
      })
    );
  }, []);

  const handleDiagnosticoCrawlerClick = useCallback(({ declaracaoImportacao }) => {
    handleOpenPopUpDiagnosticoDi({ args: { declaracaoImportacao } });
  });

  const [handleOpenPopUpDiagnosticoDi, diagnosticoDiPopUpConfig] = useConfigurePopUp({
    primaryConfirmCallback: async ({ declaracaoImportacao }) => {
      dispatch(diagnosticoDiByIdAsync({ declaracaoImportacao }));
    },
    initialOptions: {
      title: t('com.muralis.qcx.extracaoDiagnostico.titlePopUp'),
      message: t('com.muralis.qcx.transmissaoLi.messagePopUp'),
    },
  });

  const handleTransmissaoDuimp = (transmissao) => {
    dispatch(transmissaoDuimpByIdAsync(transmissao));
  };

  const handleDiagnosticarDuimp = ({ declaracaoImportacao }) => {
    dispatch(transmissaoDiagnosticoDuimpByIdAsync({ declaracaoImportacao }));
  };

  const handleRegistraDuimp = ({ declaracaoImportacao }) => {
    dispatch(registrarDuimpByIdAsync({ declaracaoImportacao }));
  };

  const registrarDuimp = (values) => {
    if (modelDuimpSelecionada && modelDuimpSelecionada.id) {
      const { id, atributosAdicionais } = modelDuimpSelecionada;

      handleRegistraDuimp({
        declaracaoImportacao: {
          id,
          processo: atributosAdicionais?.processo,
          tipo: 'REGISTRO',
          transmitirComErros: values?.transmitirComErrosNaoImpeditivos === 'SIM',
        },
      });

      setExibirModalTransmissaoDuimp(false);
    }
  };

  const [handleCalcularClick, formDialogCalculoStatus, handleFormDialogCalculoStatus] = useFormDialogSync(
    async (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(
          setRelatedCalculoModel({
            ids: data?.ids,
            id: data?.id,
            calculada: data?.calculada,
            atributosAdicionais: {
              tipo: data?.atributosAdicionais?.tipo,
              processo: data?.atributosAdicionais?.processo,
            },
            active: data?.active,
          })
        );
      }
    },
    []
  );

  const [handleOpenAliquotasDiferemPopUp, aliquotasDiferemPopUpConfig] = useConfigurePopUp({
    primaryConfirmCallback: (data) => dispatch(conferirByIdFromFaturaAsync({ ...data, ignoreAliquotas: true })),
    initialOptions: { title: 'Atenção as Alíquotas Diferem. Deseja continuar com a conferência?' },
  });

  const [handleConferirByIdClickMiddleware, operationOfConferirById] = useOperationMiddleware(
    async (data) => {
      dispatch(
        conferirByIdFromFaturaAsync({
          id: data?.id,
          onConflictCallback: handleOpenAliquotasDiferemPopUp,
          needsRefetch,
        })
      );
    },
    [needsRefetch]
  );

  const handleConfigureConferirByIdClick = useCallback(
    (row, popupState) => {
      const { id, atributosAdicionais, metaFields } = row;

      const handleConferirByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: t('com.muralis.qcx.acoes.confirmarOperacaoConferencia').toUpperCase(),
              message: t('com.muralis.qcx.confirmarOperacaoDIDUIMPConferida', {
                processo: atributosAdicionais?.processo,
              }),
              endMessage: t('com.muralis.qcx.certezaConferila'),
            },
          });

          const dataOfOperationConferir = {
            id,
            metaFields,
          };

          handleConferirByIdClickMiddleware(configureOperation, dataOfOperationConferir);
        }
      };

      return handleConferirByIdClick;
    },
    [handleConferirByIdClickMiddleware]
  );

  const [handleLiberarByIdClickMiddleware, operationOfLiberarById] = useOperationMiddleware(
    async (data) => {
      dispatch(
        liberarByIdFromFaturaAsync({
          id: data?.id,
          refetch: needsRefetch,
        })
      );
    },
    [needsRefetch]
  );

  const [handleRetransmitirByIdClickMiddleware, operationOfRetransmitirById] = useOperationMiddleware(
    async (data) => {
      dispatch(
        retransmitirByIdFromFaturaAsync({
          id: data?.id,
          refetch: needsRefetch,
        })
      );
    },
    [needsRefetch]
  );

  const handleConfigureLiberarByIdClick = useCallback(
    (row, popupState) => {
      const { id, atributosAdicionais, metaFields } = row;

      const handleLiberarByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: t('com.muralis.qcx.confirmarLiberacao').toUpperCase(),
              message: t('com.muralis.qcx.certezaLiberarRegistroDI', { processo: atributosAdicionais?.processo }),
            },
          });

          const dataOfOperationLiberar = {
            id,
            metaFields,
          };

          handleLiberarByIdClickMiddleware(configureOperation, dataOfOperationLiberar);
        }
      };

      return handleLiberarByIdClick;
    },
    [handleLiberarByIdClickMiddleware]
  );

  const handleConfigureRetransmitirByIdClick = useCallback(
    (row, popupState) => {
      const { id } = row;

      const handleRetransmitirByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: 'Confirmar operação de Retransmissão'.toUpperCase(),
              message: 'Deseja realmente retransmitir o registro DI?',
            },
          });

          const dataOfOperationRetransmitir = {
            id,
          };

          handleRetransmitirByIdClickMiddleware(configureOperation, dataOfOperationRetransmitir);
        }
      };

      return handleRetransmitirByIdClick;
    },
    [handleLiberarByIdClickMiddleware]
  );

  const [handleOpenPopUpReabrirDi, reabriDiPopUpConfig] = useConfigurePopUp({
    primaryConfirmCallback: async ({ id, processo }) => {
      const responseFollowUp = await followUpAPI.fetchByFilter([
        { name: 'processo', value: processo },
        { name: 'servicoProduto', value: 'IMPORTACAO' },
      ]);

      if (!responseFollowUp) {
        dispatch(setErrorFeedback({ message: 'Processo não encontrado.' }));
        return;
      }

      if (responseFollowUp?.status === 200) {
        const followUp = responseFollowUp?.data?.[0];
        if (followUp?.bloqueado) {
          dispatch(
            setErrorFeedback({
              message: 'Follow up Bloqueado no Financeiro',
            })
          );
          return;
        }
      } else {
        dispatch(
          setErrorFeedback({
            message: 'Erro ao buscar o follow up',
          })
        );
        return;
      }
      dispatch(reabrirByIdAsync({ id, refetch: needsRefetch }));
    },
    initialOptions: {
      title: 'Deseja reabrir a DI?',
    },
  });

  const handleReabrirClick = useCallback(({ id, processo }) => {
    handleOpenPopUpReabrirDi({ args: { id, processo } });
  });

  const [
    handleDadosRegistroSiscomexClick,
    formDialogDadosRegistroSiscomexStatus,
    handleFormDialogDadosRegistroSiscomexStatus,
  ] = useFormDialogSync(async (event, data) => {
    if (isFunction(event?.stopPropagation)) {
      event.stopPropagation();

      dispatch(
        setRelatedRegistroModel({
          id: data?.id,
          processo: data?.processo,
          registro: data?.registro,
          dataRegistroSiscomex: data?.dataRegistroSiscomex,
          numeroTransmissao: data?.numeroTransmissao,
          confirmacaoPendente: !data?.registro || !data?.dataRegistroSiscomex,
        })
      );
    }
  }, []);

  const filterAvailableFollowUp = useCallback(async () => {
    const availableFollowUpsResponse = await followUpAPI
      .fetchByFilter([
        {
          name: 'servicoProduto',
          value: TipoProdutoUtils.IMPORTACAO,
        },
        {
          name: 'filtrarVinculados',
          value: true,
        },
      ])
      .catch((reason) => {
        const errorMessage = t('com.muralis.qcx.erro.erroConsultarFollowUp', {
          dado: reason?.response && reason?.response?.data ? `: ${reason?.response?.data?.message}.` : '.',
        });
        dispatch(failure());
        dispatch(
          setErrorFeedback({
            message: errorMessage,
          })
        );
      });

    if (availableFollowUpsResponse?.status === 200) {
      const availableFollowUps = availableFollowUpsResponse?.data;

      if (_.isArrayLikeObject(availableFollowUps) && _.isEmpty(availableFollowUps)) {
        dispatch(failure());

        dispatch(
          setErrorFeedback({
            message: t('com.muralis.qcx.validacao.naoEncontradoFollowUp'),
          })
        );

        return;
      }

      dispatch(followUpActions.resetControl());
      dispatch(
        followUpActions.setList({
          data: availableFollowUps,
        })
      );
    }
  }, []);

  const [handleDuplicarClick, formDialogDuplicacaoStatus, handleFormDialogDuplicacaoStatus] = useFormDialogSync(
    async (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();
        dispatch(
          setRelatedDuplicataModel({
            id: data?.id,
            atributosAdicionais: {
              tipo: TIPO_DI_DUIMP,
            },
            meta: {
              from: {
                processo: data?.processo,
              },
            },
          })
        );
      }
    },
    [filterAvailableFollowUp]
  );

  const handleDuplicarByProcessoSubmit = useCallback(
    async (values) => {
      const response = await followUpAPI.fetchByFilter([
        { name: 'processo', value: values?.followUp?.numero },
        { name: 'servicoProduto', value: 'IMPORTACAO' },
      ]);

      const followUp = response?.data?.[0];

      dispatch(
        duplicarByIdFromFaturaAsync({
          id: values?.id,
          meta: {
            from: {
              processo: values?.meta?.from?.processo,
            },
          },
          payload: {
            followUp: {
              id: followUp?.id,
            },
            atributosAdicionais: {
              tipo: values?.atributosAdicionais?.tipo,
            },
          },
        })
      );

      handleFormDialogDuplicacaoStatus(DONE_STATUS);
    },
    [handleFormDialogDuplicacaoStatus]
  );

  const handleCloseFormDialogDuplicacao = useCallback(
    () => handleFormDialogDuplicacaoStatus(CLOSED_STATUS),
    [handleFormDialogDuplicacaoStatus]
  );

  const isFormDialogDuplicacaoOpen = useMemo(() => isOpenStatus(formDialogDuplicacaoStatus));

  const formDialogDuplicacaoContent = useMemo(
    () =>
      t('com.muralis.qcx.mensagem.preenchaNumeroNovoProcessoDuplicarDIDUIMP', {
        processo: duplicataRelatedModel?.meta?.from?.processo,
      }),
    [duplicataRelatedModel]
  );

  const fetchAll = useCallback(() => {
    dispatch(
      fetchAllDecImportWithPaginationAsync([
        { name: 'page', value: 1 },
        { name: 'size', value: 10 },
      ])
    );
  }, []);

  const [handleIncluirMultaClick, formDialogIncluirMultaStatus, handleFormDialogIncluirMultaStatus] = useFormDialogSync(
    async (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(setRelatedMultaModel({ id: data?.id, multa: data?.multa }));
      }
    },
    []
  );

  const isFormDialogIncluirMultaOpen = useMemo(() => isOpenStatus(formDialogIncluirMultaStatus));

  const handleCloseFormDialogIncluirMulta = useCallback(() => {
    handleFormDialogIncluirMultaStatus(CLOSED_STATUS);
    _.debounce(() => {
      dispatch(resetRelatedMultaModel());
    }, 300);
  }, [handleFormDialogIncluirMultaStatus]);

  const handleIncluirMultaSubmit = useCallback(
    async (data) => {
      handleFormDialogIncluirMultaStatus(AWAITING_STATUS);

      try {
        dispatch(loading());
        handleFormDialogIncluirMultaStatus(SUBMITTING_STATUS);

        const updated = {
          id: data?.id,
          atributosAdicionais: {
            tipo: TIPO_DI_DUIMP,
            multa: {
              valorMultaReais: normalizeNumeral(data?.multa?.valorMultaReais),
              tipoRateioMulta: data?.multa?.tipoRateioMulta,
            },
          },
        };

        const response = await declaracaoImportacaoAPI.save(updated);

        if (response?.status === 200) {
          handleFormDialogIncluirMultaStatus(DONE_STATUS);
          dispatch(resetRelatedMultaModel());
          fetchAll();

          const savedMessage = 'Multa incluída no processo.';

          dispatch(success());
          dispatch(
            setSuccessFeedback({
              message: savedMessage,
            })
          );
        }
      } catch (error) {
        dispatch(failure());
        handleFormDialogIncluirMultaStatus(FAIL_STATUS);
        dispatch(
          setErrorFeedback({
            message: 'Erro ao incluir multa no processo.',
          })
        );
      }
    },
    [handleFormDialogIncluirMultaStatus]
  );

  const [handleEmissaoIcmsClick, formDialogEmissaoIcmsStatus, handleFormDialogEmissaoIcmsStatus] = useFormDialogSync(
    async (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(
          setRelatedEmissaoIcmsModel({
            id: data?.id,
            processo: data?.processo,
            registro: data?.registro,
            dataVencimentoIcms: data?.dataVencimentoIcms,
            numeroConvenioProtocoloEspecificacaoMercadoria: data?.numeroConvenioProtocoloEspecificacaoMercadoria,
            confirmacaoPendente: !data?.dataVencimentoIcms,
            geracaoGareEmissaoIcms: data?.geracaoGareEmissaoIcms,
            geraSP: !!data?.geraSpIcms,
          })
        );
      }
    },
    []
  );

  const isFormDialogEmissaoIcmsOpen = useMemo(() => isOpenStatus(formDialogEmissaoIcmsStatus));

  const formDialogEmissaoIcmsContent = useMemo(
    () =>
      emissaoIcmsRelatedModel?.confirmacaoPendente || !emissaoIcmsRelatedModel?.dataVencimentoIcms
        ? t('com.muralis.qcx.mensagem.dataRelacionadaVencimentoPagamentoICMS')
        : t('com.muralis.qcx.mensagem.dataVencimentoPagamentoICMSSEFAZ'),
    [emissaoIcmsRelatedModel]
  );

  const handleCloseFormDialogEmissaoIcms = useCallback(() => {
    handleFormDialogEmissaoIcmsStatus(CLOSED_STATUS);
    _.debounce(() => {
      dispatch(resetRelatedEmissaoIcmsModel());
    }, 300);
  }, [handleFormDialogEmissaoIcmsStatus]);

  const [handleEmissaoIcmsByIdClickMiddleware, operationOfEmissaoIcms] = useOperationMiddleware(
    async (data) => {
      try {
        dispatch(loading());
        handleFormDialogEmissaoIcmsStatus(SUBMITTING_STATUS);

        const updated = {
          id: data?.id,
          atributosAdicionais: {
            tipo: TIPO_DI_DUIMP,
            dataVencimentoIcms: normalizeData(data?.dataVencimentoIcms),
            numeroConvenioProtocoloEspecificacaoMercadoria: data?.numeroConvenioProtocoloEspecificacaoMercadoria,
            geraSpIcms: !!data?.geraSP,
          },
        };

        const glmeSpPayload = {
          id: data?.id,
        };

        const icmsRioPayload = {
          id: data?.id,
          dataPagamento: normalizeData(data?.dataVencimentoIcms, 'DD/MM/YYYY'),
        };

        const response = await declaracaoImportacaoAPI.save(updated);
        const glmeSpResponse = await declaracaoImportacaoAPI.emitirGlmeSp(glmeSpPayload);
        const icmsRioResponse = await declaracaoImportacaoAPI.emitirIcmsRio(icmsRioPayload);
        const glmeMgResponse = await declaracaoImportacaoAPI.emitirGlmeMg({
          id: data?.id,
          geraSP: data?.geraSP,
          tenant: response?.data?.tennantUnidadeDeNegocio?.id,
        });

        if (glmeMgResponse.status === 200) {
          handleDownloader(glmeMgResponse);

          const soliStatus = (glmeMgResponse?.headers?.['x-solicitacao-status'] || '').toUpperCase();
          const soliError = glmeMgResponse?.headers?.['x-solicitacao-error'];

          if (soliStatus === 'ERROR') {
            dispatch(
              setErrorFeedback({
                message: `Guia GLME gerada, mas houve erro ao gerar a Solicitação de Pagamento: ${
                  soliError || 'Erro desconhecido.'
                }`,
              })
            );
          } else if (soliStatus === 'OK') {
            dispatch(
              setSuccessFeedback({
                message: 'Guia GLME e Solicitação de Pagamento geradas com sucesso.',
              })
            );
          }
        }

        if (response?.status === 200 && icmsRioResponse?.status === 200) {
          handleFormDialogEmissaoIcmsStatus(DONE_STATUS);
          dispatch(resetRelatedEmissaoIcmsModel());
          fetchAll();

          const savedMessage = data?.numeroConvenioProtocoloEspecificacaoMercadoria
            ? t('com.muralis.qcx.mensagem.dataVencmentoEmissaoICMSDIDUIMPSalvos', { registro: data?.registro })
            : t('com.muralis.qcx.mensagem.dataVencimentoEmissaoICMSDIDUIMPSalva', { registro: data?.registro });

          dispatch(success());
          if (
            glmeMgResponse?.status !== 200 ||
            (glmeMgResponse?.headers?.['x-solicitacao-status'] || '') === 'SKIPPED'
          ) {
            dispatch(
              setSuccessFeedback({
                message: savedMessage,
              })
            );
          }
        }
      } catch (error) {
        dispatch(failure());
        handleFormDialogEmissaoIcmsStatus(FAIL_STATUS);
        let errorMessage = t('com.muralis.qcx.naoPossivelSalvarDadosEmissaoICMSDIDUIMP', { registro: data?.registro });
        if (error?.response && error?.response?.data) {
          errorMessage = t('com.muralis.qcx.naoPossivelSalvarDadosEmissaoICMSDIDUIMPMensagem', {
            registro: data?.registro,
            erro: error?.response?.data?.message,
          });
        }

        dispatch(
          setErrorFeedback({
            message: errorMessage,
          })
        );
      }
    },
    [formDialogEmissaoIcmsStatus, handleFormDialogEmissaoIcmsStatus]
  );

  const handleConfigureEmissaoIcmsByIdClick = useCallback(
    (values) => {
      const formattedDate = formatDate(values?.dataVencimentoIcms);

      const hasNumeroConvenioProtocoloEspecificacaoMercadoria =
        !!values?.numeroConvenioProtocoloEspecificacaoMercadoria;

      const configureOperation = () => ({
        options: {
          title: t('com.muralis.qcx.DIDUIMP.autorizarOperacaoDIDUIMP', { numero: values?.registro }),
          message: hasNumeroConvenioProtocoloEspecificacaoMercadoria
            ? t('com.muralis.qcx.mensagem.confirmarDataVencimentoEspecificacaoMercadoria', {
                data: formattedDate,
                mercadoria: values?.numeroConvenioProtocoloEspecificacaoMercadoria,
              })
            : t('com.muralis.qcx.mensagem.confirmarDataVencimento', { data: formattedDate }),
          endMessage: hasNumeroConvenioProtocoloEspecificacaoMercadoria
            ? t('com.muralis.qcx.DIDUIMP.certezaUtilizalosEmissaoICMS')
            : t('com.muralis.qcx.DIDUIMP.certezaUtilizalaEmissaoICMS'),
        },
      });

      handleEmissaoIcmsByIdClickMiddleware(configureOperation, values);
    },
    [formDialogEmissaoIcmsStatus, handleFormDialogEmissaoIcmsStatus, handleEmissaoIcmsByIdClickMiddleware]
  );

  const handleEmissaoIcmsSubmit = useCallback(
    async (values) => {
      handleFormDialogEmissaoIcmsStatus(AWAITING_STATUS);

      dispatch(
        setRelatedEmissaoIcmsModel({
          ...values,
          confirmacaoPendente: true,
        })
      );
      handleConfigureEmissaoIcmsByIdClick(values);
    },
    [handleFormDialogEmissaoIcmsStatus, handleConfigureEmissaoIcmsByIdClick]
  );

  const handleDownloader = async (response) => {
    if (response.status === 204) {
      return;
    }

    if (response.status === 200) {
      const blob = new Blob([response.data]);

      const contentDisposition = response?.headers['content-disposition'] || '';

      const [matched] = contentDisposition.match(/filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/g);

      const fileName = matched && _.isString(matched) ? matched.replace(/filename=|"/g, '') : undefined;

      const urlBlob = window.URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.href = urlBlob;

      a.download = fileName;

      document.body.appendChild(a);

      a.click();

      a.remove();

      window.URL.revokeObjectURL(urlBlob);
    }
  };

  const handleCloseEmissaoIcmsOperation = useCallback(() => {
    const callback = () => {
      if (isAwaitingStatus(formDialogEmissaoIcmsStatus)) {
        handleFormDialogEmissaoIcmsStatus(OPEN_STATUS);
      }
    };

    operationOfEmissaoIcms?.reset(callback);
  }, [operationOfEmissaoIcms, formDialogEmissaoIcmsStatus, handleFormDialogEmissaoIcmsStatus]);

  const [handleActivateOrInactivateByIdClickMiddleware, operationOfActivateOrInactivateById] = useOperationMiddleware(
    async (data) => {
      const isInactivation = data?.active;

      if (isInactivation) {
        dispatch(inactivateByIdFromFaturaAsync(data?.id));

        return;
      }

      dispatch(activateByIdFromFaturaAsync(data?.id));
    },
    [declaracoesImportacao]
  );

  const handleConfigureActivateOrInactivateByIdClick = useCallback(
    (row, popupState) => {
      const { id, atributosAdicionais, active } = row;

      const handleActivateOrInactivateByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: t('com.muralis.qcx.acoes.confirmarOperacaoExclusao').toUpperCase(),
              message: t('com.muralis.qcx.certezaExcluirRecuperarDI', {
                acao: active
                  ? t('com.muralis.qcx.acoes.excluir').toLowerCase()
                  : t('com.muralis.qcx.acoes.recuperar').toLowerCase(),
                processo: atributosAdicionais?.processo,
              }),
            },
          });

          const dataOfOperationActivateOrInactivate = {
            id,
            active,
          };

          handleActivateOrInactivateByIdClickMiddleware(configureOperation, dataOfOperationActivateOrInactivate);
        }
      };

      return handleActivateOrInactivateByIdClick;
    },
    [handleActivateOrInactivateByIdClickMiddleware]
  );

  const handleDeleteTransmissaoDuimpByClick = useCallback(({ declaracaoImportacao }) => {
    handleOpenDeleteTransmissaoDuimp({ args: { declaracaoImportacao } });
  });

  const [handleOpenDeleteTransmissaoDuimp, deleteTransmissaoDuimpPopup] = useConfigurePopUp({
    primaryConfirmCallback: async ({ declaracaoImportacao }) => {
      dispatch(apagarTransmissaoDuimpAsync({ declaracaoImportacao }));
    },
    initialOptions: {
      title: 'Deseja excluir a transmissão da DUIMP?',
      message: 'A transmissão da duimp será excluída do sistema e no portal único. Deseja continuar ?',
    },
  });

  const formDialogCalculoContent = useMemo(
    () =>
      t('com.muralis.qcx.mensagem.inserirDataCalcularRecalcularDIDUIMP', {
        acao: isNaoCalculadaStatus(calculoRelatedModel?.calculada)
          ? t('com.muralis.qcx.acoes.calcular').toLowerCase()
          : t('com.muralis.qcx.acoes.recalcular').toLowerCase(),
        processo: calculoRelatedModel?.atributosAdicionais?.processo,
      }),
    [calculoRelatedModel]
  );

  const isFormDialogCalculoOpen = useMemo(() => isOpenStatus(formDialogCalculoStatus));

  const handleCloseFormDialogCalculo = useCallback(
    () => handleFormDialogCalculoStatus(CLOSED_STATUS),
    [handleFormDialogCalculoStatus]
  );

  const handleErrorBulkOperation = useCallback((errors) => {
    // errors.forEach((error) => {
    //   dispatch(setErrorFeedback({ message: error }));
    // });
    dispatch(success());
  }, []);

  const handleSuccessBulkOperation = useCallback(() => {
    handleFormDialogCalculoStatus(DONE_STATUS);
    dispatch(resetMode());
    dispatch(success());
    // fetchAll();
  });

  const handleBulkCalculateSubmit = useCallback(
    (values) => {
      const data = values?.ids.map((id) => ({
        id,
        dataCalculo: normalizeData(values?.atributosAdicionais?.dataCalculo),
      }));

      handleFormDialogCalculoStatus(SUBMITTING_STATUS);
      dispatch(loadingDI());

      dispatch(
        calcularDisLoteAsync({
          data,
          successCallback: handleSuccessBulkOperation,
          errorCallback: handleErrorBulkOperation,
        })
      );
    },
    [handleFormDialogCalculoStatus, handleSuccessBulkOperation, handleErrorBulkOperation]
  );

  const handleCalcularByDataSubmitAlert = useCallback(
    async (values) => {
      try {
        const response = await declaracaoImportacaoAPI.checkAcrescimosDeducoes(values?.id);

        if (response.status < 300) {
          const needsAlert = response.data;

          if (needsAlert === true) {
            dispatch(
              setRelatedCalculoModel({
                ...calculoRelatedModel,
                atributosAdicionais: {
                  ...calculoRelatedModel.atributosAdicionais,
                  dataCalculo: values?.atributosAdicionais?.dataCalculo,
                },
              })
            );

            handleCloseFormDialogCalculo();
            setIsCalcularAcrescimosDeducoesAlertOpen(true);
            return;
          }
        }
      } catch (err) {
        // silent catch
      }
      handleCalcularByDataSubmit(values);
    },
    [calculoRelatedModel]
  );

  const handleCalcularByDataSubmit = useCallback(
    async (values) => {
      handleCloseAlertAcrescimosDeducoes();

      try {
        dispatch(loading());

        handleFormDialogCalculoStatus(SUBMITTING_STATUS);

        const payload = {
          atributosAdicionais: {
            tipo: values?.atributosAdicionais?.tipo,
            dataCalculo: normalizeData(values?.atributosAdicionais?.dataCalculo),
          },
        };

        const response = await faturaAPI.calculateById(values?.id, [DECLARACAO_IMPORTACAO_ENDPOINT], payload);

        if (response.status === 200) {
          const isNaoCalculada = isNaoCalculadaStatus(values?.calculada);
          const processo = values?.atributosAdicionais?.processo;
          const responseMessage = t('com.muralis.qcx.mensagem.DIEstado', {
            processo,
            estado: isNaoCalculada ? t('com.muralis.qcx.estado.calculada') : t('com.muralis.qcx.estado.recalculada'),
          });

          dispatch(success());
          dispatch(
            updateOnList({
              data: response?.data,
            })
          );
          dispatch(setSuccessFeedback({ message: responseMessage }));

          if (needsRefetch) {
            fetchFaturaDiDuimpCapaByFaturaId(values?.id);
          }

          if (isNaoCalculada) {
            dispatch(
              changeLastHistoryModel({
                processo: values?.atributosAdicionais?.processo,
              })
            );
          }

          handleFormDialogCalculoStatus(DONE_STATUS);
        }
      } catch (error) {
        handleFormDialogCalculoStatus(FAIL_STATUS);

        const isUnknownError =
          isEmpty(error?.response) || isEmpty(error?.response?.data) || isEmpty(error?.response?.data?.message);

        const isNaoCalculada = isNaoCalculadaStatus(values?.calculada);
        const errorMessage = isUnknownError
          ? t('com.muralis.qcx.erro.erroCalcularRecalcular', {
              estado: isNaoCalculada ? t('com.muralis.qcx.acoes.calcular') : t('com.muralis.qcx.acoes.recalcular'),
            })
          : t('com.muralis.qcx.erro.erroCalcularRecalcularEspecifico', {
              estado: isNaoCalculada ? t('com.muralis.qcx.acoes.calcular') : t('com.muralis.qcx.acoes.recalcular'),
              mensagem: error?.response?.data?.message,
            });

        dispatch(failure());
        dispatch(
          setErrorFeedback({
            message: errorMessage,
          })
        );
      }
    },
    [handleFormDialogCalculoStatus, needsRefetch]
  );

  const handleAtualizarDadosCarga = useCallback((faturaId, viaTransporteDescription) => {
    dispatch(atualizaDadosCargaAsync(faturaId, viaTransporteDescription));
  }, []);

  const handleObterChavesDuimp = ({ declaracaoImportacao }) => {
    dispatch(obterChavesDuimpAsync({ declaracaoImportacao }));
  };

  const handleRetificarDuimp = ({ declaracaoImportacao }) => {
    dispatch(retificarDuimpAsync({ declaracaoImportacao }));
  };

  const handleRecuperarStatusDuimp = ({ declaracaoImportacao }) => {
    dispatch(recuperarStatusDuimpAsync({ declaracaoImportacao }));
  };

  const handleOpenModalTransmissao = async ({ id, atributosAdicionais }) => {
    if (id) {
      const response = await fetchDataDiagnostico(id);

      if (response.message !== null) {
        dispatch(
          setErrorFeedback({
            message: t(`${response.message}`),
          })
        );
      } else {
        setDataDiagnostico(response.data);
        setProcessoDiagnostico(atributosAdicionais?.processo);
        setIdDiToTransmissao(id);
        setIsDuimpSelected(atributosAdicionais?.tipoDeclaracao?.code === '50');
        setFaturaSelected(atributosAdicionais);
        setExibirModalTransmissao(true);
      }
    }
  };

  const transmitirDuimpSemTributos = async () => {
    const payload = {
      identificacao: {
        numero: dataDiagnostico?.diagnostico?.[0]?.protocolo,
        versao: '0',
      },
      unidadeNegocioId: unidadeNegocioSelecionada.id,
      isTransmitido: true,
      transmitirComErros: false,
    };

    await doPutQCXRequest(
      ['diduimp', 'v2', 'transmissao-duimp', idDiToTransmissao, 'capa', 'finalizada?transmitirSemTributos=1'],
      payload
    );
  };

  const unnormalize = useCallback((normalizedData) => {
    const { acrescimosDeducoes } = normalizedData;

    const normalizedAcrescimosAduaneiros = acrescimosDeducoes || [];

    const adicaoDuimp = {
      ...normalizedData,
      valorFornecedor: isValid(normalizedData?.valorFornecedor)
        ? unnormalizeNumeral(normalizedData?.valorFornecedor, formatBrazilianNumericDecimal(2))
        : undefined,
      pesoLiquido: isValid(normalizedData?.pesoLiquido)
        ? unnormalizeNumeral(normalizedData?.pesoLiquido, formatBrazilianNumericDecimal(5))
        : undefined,
      pesoBruto: isValid(normalizedData?.pesoBruto)
        ? unnormalizeNumeral(normalizedData?.pesoBruto, formatBrazilianNumericDecimal(5))
        : undefined,
      quantidadeEstatistica: isValid(normalizedData?.quantidadeEstatistica)
        ? unnormalizeNumeral(normalizedData?.quantidadeEstatistica, formatBrazilianNumericDecimal(7))
        : undefined,
      valorMleMoeda: isValid(normalizedData?.valorMleMoeda)
        ? unnormalizeNumeral(normalizedData?.valorMleMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorMleReal: isValid(normalizedData?.valorMleReal)
        ? unnormalizeNumeral(normalizedData?.valorMleReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorDespesaMoeda: isValid(normalizedData?.valorDespesaMoeda)
        ? unnormalizeNumeral(normalizedData?.valorDespesaMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorDespesaReal: isValid(normalizedData?.valorDespesaReal)
        ? unnormalizeNumeral(normalizedData?.valorDespesaReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorCifReal: isValid(normalizedData?.valorCifReal)
        ? unnormalizeNumeral(normalizedData?.valorCifReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorMcvMoeda: isValid(normalizedData?.valorMcvMoeda)
        ? unnormalizeNumeral(normalizedData?.valorMcvMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorMcvReal: isValid(normalizedData?.valorMcvReal)
        ? unnormalizeNumeral(normalizedData?.valorMcvReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorFreteTerritorioNacionalMoeda: isValid(normalizedData?.valorFreteTerritorioNacionalMoeda)
        ? unnormalizeNumeral(normalizedData?.valorFreteTerritorioNacionalMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorFreteTerritorioNacionalReal: isValid(normalizedData?.valorFreteTerritorioNacionalReal)
        ? unnormalizeNumeral(normalizedData?.valorFreteTerritorioNacionalReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorSeguroMoeda: isValid(normalizedData?.valorSeguroMoeda)
        ? unnormalizeNumeral(normalizedData?.valorSeguroMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorSeguroReal: isValid(normalizedData?.valorSeguroReal)
        ? unnormalizeNumeral(normalizedData?.valorSeguroReal, formatBrazilianNumericDecimal(2))
        : undefined,
      valorFreteMoeda: isValid(normalizedData?.valorFreteMoeda)
        ? unnormalizeNumeral(normalizedData?.valorFreteMoeda, formatBrazilianNumericDecimal(2))
        : undefined,
      valorFreteReal: isValid(normalizedData?.valorFreteReal)
        ? unnormalizeNumeral(normalizedData?.valorFreteReal, formatBrazilianNumericDecimal(2))
        : undefined,
      exportador: normalizedData?.exportador?.id ? normalizedData?.exportador : undefined,
      fabricante: normalizedData?.fabricante?.id ? normalizedData?.fabricante : undefined,
      paisOrigem: normalizedData?.paisOrigem?.id ? normalizedData?.paisOrigem : undefined,
      instituicaoFinanciadora: normalizedData?.instituicaoFinanciadora?.id
        ? normalizedData?.instituicaoFinanciadora
        : undefined,
      motivoImportacaoSemCoberturaCambial: normalizedData?.motivoImportacaoSemCoberturaCambial?.id
        ? normalizedData?.motivoImportacaoSemCoberturaCambial
        : undefined,
      moeda: normalizedData?.moeda?.id ? normalizedData?.moeda : undefined,
      moedaDespesa: normalizedData?.moedaDespesa?.id ? normalizedData?.moedaDespesa : undefined,
      ncm: normalizedData?.ncm?.id ? normalizedData?.ncm : undefined,
      naladiNcca: normalizedData?.naladiNcca?.id ? normalizedData?.naladiNcca : undefined,
      naladiSh: normalizedData?.naladiSh?.id ? normalizedData?.naladiSh : undefined,
      operacaoFiscal: normalizedData?.operacaoFiscal?.id ? normalizedData?.operacaoFiscal : undefined,
      atributos: (normalizedData?.atributos || [])?.map((atributo) =>
        atributo?.id
          ? {
              ...atributo,
              nve: atributo?.nve?.id ? atributo?.nve : undefined,
              atributo: atributo?.atributo?.id ? atributo?.atributo : undefined,
              especificacao: atributo?.especificacao?.id ? atributo?.especificacao : undefined,
            }
          : {
              ...atributo,
              nve: atributo?.nve?.id ? atributo?.nve : undefined,
              atributo: atributo?.atributo?.id ? atributo?.atributo : undefined,
              especificacao: atributo?.especificacao?.id ? atributo?.especificacao : undefined,
              id: uuid(),
            }
      ),
      declaracaoExportacoes: (normalizedData.declaracaoExportacoes || [])?.map((de) =>
        de?.id
          ? {
              ...de,
              qtdUnidadeEstatistica: isValid(de?.qtdUnidadeEstatistica)
                ? unnormalizeNumeral(de?.qtdUnidadeEstatistica, formatBrazilianNumericDecimal(7))
                : undefined,
            }
          : {
              ...de,
              id: uuid(),
              qtdUnidadeEstatistica: isValid(de?.qtdUnidadeEstatistica)
                ? unnormalizeNumeral(de?.qtdUnidadeEstatistica, formatBrazilianNumericDecimal(7))
                : undefined,
            }
      ),
      acrescimosDeducoes: normalizedAcrescimosAduaneiros.map((acrescimoAduaneiro) => ({
        ...acrescimoAduaneiro,
        valorMoeda: isValid(acrescimoAduaneiro?.valorMoeda)
          ? unnormalizeNumeral(acrescimoAduaneiro?.valorMoeda, formatBrazilianNumericDecimal(2))
          : undefined,
        valorReal: isValid(acrescimoAduaneiro?.valorReal)
          ? unnormalizeNumeral(acrescimoAduaneiro?.valorReal, formatBrazilianNumericDecimal(2))
          : undefined,
        acrescimoValoracaoAduaneira: !acrescimoAduaneiro?.acrescimoValoracaoAduaneira
          ? acrescimoAduaneiro?.deducaoValoracaoAduaneira
          : acrescimoAduaneiro?.acrescimoValoracaoAduaneira,
      })),
      incotermCondicaoVenda: normalizedData?.incotermCondicaoVenda?.id
        ? normalizedData?.incotermCondicaoVenda
        : undefined,
      metodoValoracaoAduaneira: normalizedData?.metodoValoracaoAduaneira?.id
        ? normalizedData?.metodoValoracaoAduaneira
        : undefined,
      portoAeroporto: normalizedData?.portoAeroporto?.id ? normalizedData?.portoAeroporto : undefined,
      antidumping: {
        ...normalizedData?.antidumping,
        orgaoEmissorAtoLegal: normalizedData?.antidumping?.orgaoEmissorAtoLegal?.id
          ? normalizedData?.antidumping?.orgaoEmissorAtoLegal
          : undefined,
        unidadeDeMedida: normalizedData?.antidumping?.unidadeDeMedida?.id
          ? normalizedData?.antidumping?.unidadeDeMedida
          : undefined,
        tipoAtoLegal: normalizedData?.antidumping?.tipoAtoLegal?.id
          ? normalizedData?.antidumping?.tipoAtoLegal
          : undefined,
        ano: `${normalizedData?.antidumping?.ano || ''}`,
        aliquota: isValid(normalizedData?.antidumping?.aliquota)
          ? unnormalizeNumeral(normalizedData?.antidumping?.aliquota, formatBrazilianNumericDecimal(2))
          : undefined,
        valorReal: isValid(normalizedData?.antidumping?.valorReal)
          ? unnormalizeNumeral(normalizedData?.antidumping?.valorReal, formatBrazilianNumericDecimal(5))
          : undefined,
        quantidade: isValid(normalizedData?.antidumping?.quantidade)
          ? unnormalizeNumeral(normalizedData?.antidumping?.quantidade)
          : undefined,
        baseCalculoReal: isValid(normalizedData?.antidumping?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.antidumping?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.antidumping?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.antidumping?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.antidumping?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.antidumping?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      icms: {
        ...normalizedData?.icms,
        regimeTributacao: normalizedData?.icms?.regimeTributacao?.id
          ? normalizedData?.icms?.regimeTributacao
          : undefined,
        dispositivoLegal: normalizedData?.icms?.dispositivoLegal?.id
          ? normalizedData?.icms?.dispositivoLegal
          : undefined,
        baseCalculoReal: isValid(normalizedData?.icms?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.icms?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquota: isValid(normalizedData?.icms?.aliquota)
          ? unnormalizeNumeral(normalizedData?.icms?.aliquota, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaAntecipadaPercentual: isValid(normalizedData?.icms?.aliquotaAntecipadaPercentual)
          ? unnormalizeNumeral(normalizedData?.icms?.aliquotaAntecipadaPercentual, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaOriginal: isValid(normalizedData?.icms?.aliquotaOriginal)
          ? unnormalizeNumeral(normalizedData?.icms?.aliquotaOriginal, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaFecp: isValid(normalizedData?.icms?.aliquotaFecp)
          ? unnormalizeNumeral(normalizedData?.icms?.aliquotaFecp, formatBrazilianNumericDecimal(7))
          : undefined,
        percentualReducao: isValid(normalizedData?.icms?.percentualReducao)
          ? unnormalizeNumeral(normalizedData?.icms?.percentualReducao, formatBrazilianNumericDecimal(7))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.icms?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.icms?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.icms?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.icms?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        recolherPermanencia: isValid(normalizedData?.icms?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.icms?.recolherPermanencia, formatBrazilianNumericDecimal(2))
          : undefined,
        percentualBeneficio: isValid(normalizedData?.icms?.percentualBeneficio)
          ? unnormalizeNumeral(normalizedData?.icms?.percentualBeneficio, formatBrazilianNumericDecimal(7))
          : undefined,
        baseSubstTrib: isValid(normalizedData?.icms?.baseSubstTrib)
          ? unnormalizeNumeral(normalizedData?.icms?.baseSubstTrib, formatBrazilianNumericDecimal(2))
          : undefined,
        percMva: isValid(normalizedData?.icms?.percMva)
          ? unnormalizeNumeral(normalizedData?.icms?.percMva, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoSubstTrib: isValid(normalizedData?.icms?.valorDevidoSubstTrib)
          ? unnormalizeNumeral(normalizedData?.icms?.valorDevidoSubstTrib, formatBrazilianNumericDecimal(2))
          : undefined,
        substTributaria: isValid(normalizedData?.icms?.substTributaria)
          ? unnormalizeNumeral(normalizedData?.icms?.substTributaria, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      ibs: {
        ...normalizedData?.ibs,
        regimeTributacao: normalizedData?.ibs?.regimeTributacao?.id ? normalizedData?.ibs?.regimeTributacao : undefined,
        baseCalculoReal: isValid(normalizedData?.ibs?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.ibs?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquota: isValid(normalizedData?.ibs?.aliquota)
          ? unnormalizeNumeral(normalizedData?.ibs?.aliquota, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzida: isValid(normalizedData?.ibs?.aliquotaReduzida)
          ? unnormalizeNumeral(normalizedData?.ibs?.aliquotaReduzida, formatBrazilianNumericDecimal(7))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.ibs?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.ibs?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.ibs?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.ibs?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      cbs: {
        ...normalizedData?.cbs,
        regimeTributacao: normalizedData?.cbs?.regimeTributacao?.id ? normalizedData?.cbs?.regimeTributacao : undefined,
        baseCalculoReal: isValid(normalizedData?.cbs?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.cbs?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquota: isValid(normalizedData?.cbs?.aliquota)
          ? unnormalizeNumeral(normalizedData?.cbs?.aliquota, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzida: isValid(normalizedData?.cbs?.aliquotaReduzida)
          ? unnormalizeNumeral(normalizedData?.cbs?.aliquotaReduzida, formatBrazilianNumericDecimal(7))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.cbs?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.cbs?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.cbs?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.cbs?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      pisCofins: {
        ...normalizedData?.pisCofins,
        unidadeDeMedidaPisPasep: normalizedData?.pisCofins?.unidadeDeMedidaPisPasep?.id
          ? normalizedData?.pisCofins?.unidadeDeMedidaPisPasep
          : undefined,
        unidadeDeMedidaCofins: normalizedData?.pisCofins?.unidadeDeMedidaCofins?.id
          ? normalizedData?.pisCofins?.unidadeDeMedidaCofins
          : undefined,
        regimeTributacao: normalizedData?.pisCofins?.regimeTributacao?.id
          ? normalizedData?.pisCofins?.regimeTributacao
          : undefined,
        fundamentoLegalRegimeTributacaoPisCofins: normalizedData?.pisCofins?.fundamentoLegalRegimeTributacaoPisCofins
          ?.id
          ? normalizedData?.pisCofins?.fundamentoLegalRegimeTributacaoPisCofins
          : undefined,
        baseCalculoReal: isValid(normalizedData?.pisCofins?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        percentualReducao: isValid(normalizedData?.pisCofins?.percentualReducao)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.percentualReducao, formatBrazilianNumericDecimal(7))
          : undefined,
        percentualReducaoBaseCalculo: isValid(normalizedData?.pisCofins?.percentualReducaoBaseCalculo)
          ? unnormalizeNumeral(
              normalizedData?.pisCofins?.percentualReducaoBaseCalculo,
              formatBrazilianNumericDecimal(7)
            )
          : undefined,
        aliquotaPisPasep: isValid(normalizedData?.pisCofins?.aliquotaPisPasep)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.aliquotaPisPasep, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzidaPisPasep: isValid(normalizedData?.pisCofins?.aliquotaReduzidaPisPasep)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.aliquotaReduzidaPisPasep, formatBrazilianNumericDecimal(7))
          : undefined,
        valorRecolherPisPasepReal: isValid(normalizedData?.pisCofins?.valorRecolherPisPasepReal)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.valorRecolherPisPasepReal, formatBrazilianNumericDecimal(2))
          : undefined,
        quantidadePisPasep: isValid(normalizedData?.pisCofins?.quantidadePisPasep)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.quantidadePisPasep, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquotaCofins: isValid(normalizedData?.pisCofins?.aliquotaCofins)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.aliquotaCofins, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzidaCofins: isValid(normalizedData?.pisCofins?.aliquotaReduzidaCofins)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.aliquotaReduzidaCofins, formatBrazilianNumericDecimal(7))
          : undefined,
        valorRecolherCofinsReal: isValid(normalizedData?.pisCofins?.valorRecolherCofinsReal)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.valorRecolherCofinsReal, formatBrazilianNumericDecimal(2))
          : undefined,
        quantidadeCofins: isValid(normalizedData?.pisCofins?.quantidadeCofins)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.quantidadeCofins, formatBrazilianNumericDecimal(7))
          : undefined,
        valorDevidoPisPasepReal: isValid(normalizedData?.pisCofins?.valorDevidoPisPasepReal)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.valorDevidoPisPasepReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoCofinsReal: isValid(normalizedData?.pisCofins?.valorDevidoCofinsReal)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.valorDevidoCofinsReal, formatBrazilianNumericDecimal(2))
          : undefined,
        recolherPermanenciaCofins: isValid(normalizedData?.pisCofins?.recolherPermanenciaCofins)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.recolherPermanenciaCofins, formatBrazilianNumericDecimal(2))
          : undefined,
        recolherPermanenciaPisPasep: isValid(normalizedData?.pisCofins?.recolherPermanenciaPisPasep)
          ? unnormalizeNumeral(normalizedData?.pisCofins?.recolherPermanenciaPisPasep, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      ipi: {
        ...normalizedData?.ipi,
        tipoAtoLegalFundamentoLegal: normalizedData?.ipi?.tipoAtoLegalFundamentoLegal?.id
          ? normalizedData?.ipi?.tipoAtoLegalFundamentoLegal
          : undefined,
        orgaoEmissorFundamentoLegal: normalizedData?.ipi?.orgaoEmissorFundamentoLegal?.id
          ? normalizedData?.ipi?.orgaoEmissorFundamentoLegal
          : undefined,
        numeroAtoFundamentoLegal: normalizedData?.ipi?.numeroAtoFundamentoLegal,
        anoFundamentoLegal: `${normalizedData?.ipi?.anoFundamentoLegal || ''}`,
        numeroExFundamentoLegal: `${normalizedData?.ipi?.numeroExFundamentoLegal || ''}`,

        orgaoEmissorAtoLegal: normalizedData?.ipi?.orgaoEmissorAtoLegal?.id
          ? normalizedData?.ipi?.orgaoEmissorAtoLegal
          : undefined,
        regimeTributacao: normalizedData?.ipi?.regimeTributacao?.id ? normalizedData?.ipi?.regimeTributacao : undefined,
        tipoRecipiente: normalizedData?.ipi?.tipoRecipiente?.id ? normalizedData?.ipi?.tipoRecipiente : undefined,
        unidadeDeMedida: normalizedData?.ipi?.unidadeDeMedida?.id ? normalizedData?.ipi?.unidadeDeMedida : undefined,
        baseCalculoReal: isValid(normalizedData?.ipi?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.ipi?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquota: isValid(normalizedData?.ipi?.aliquota)
          ? unnormalizeNumeral(normalizedData?.ipi?.aliquota, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzida: isValid(normalizedData?.ipi?.aliquotaReduzida)
          ? unnormalizeNumeral(normalizedData?.ipi?.aliquotaReduzida, formatBrazilianNumericDecimal(7))
          : undefined,
        valorUnidadeMedidaReal: isValid(normalizedData?.ipi?.valorUnidadeMedidaReal)
          ? unnormalizeNumeral(normalizedData?.ipi?.valorUnidadeMedidaReal, formatBrazilianNumericDecimal(2))
          : undefined,
        quantidade: isValid(normalizedData?.ipi?.quantidade)
          ? unnormalizeNumeral(normalizedData?.ipi?.quantidade, formatBrazilianNumericDecimal(7))
          : undefined,
        capacidade: isValid(normalizedData?.ipi?.capacidade)
          ? unnormalizeNumeral(normalizedData?.ipi?.capacidade, formatBrazilianNumericDecimal(7))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.ipi?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.ipi?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.ipi?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.ipi?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
        recolherPermanencia: isValid(normalizedData?.ipi?.recolherPermanencia)
          ? unnormalizeNumeral(normalizedData?.ipi?.recolherPermanencia, formatBrazilianNumericDecimal(2))
          : undefined,
        tipoAtoLegalExTarifario: normalizedData?.ipi?.tipoAtoLegalExTarifario?.id
          ? normalizedData?.ipi?.tipoAtoLegalExTarifario
          : undefined,
        anoExTarifario: `${normalizedData?.ipi?.anoExTarifario || ''}`,
      },
      ii: {
        ...normalizedData?.ii,
        fundamentoLegalRegimeTributacaoIi: normalizedData?.ii?.fundamentoLegalRegimeTributacaoIi?.id
          ? normalizedData?.ii?.fundamentoLegalRegimeTributacaoIi
          : undefined,
        tipoAtoLegalExTarifario: normalizedData?.ii?.tipoAtoLegalExTarifario?.id
          ? normalizedData?.ii?.tipoAtoLegalExTarifario
          : undefined,
        tipoAtoLegalAcordoTarifario: normalizedData?.ii?.tipoAtoLegalAcordoTarifario?.id
          ? normalizedData?.ii?.tipoAtoLegalAcordoTarifario
          : undefined,
        regimeTributacao: normalizedData?.ii?.regimeTributacao?.id ? normalizedData?.ii?.regimeTributacao : undefined,
        ano: `${normalizedData?.ii?.ano || ''}`,
        anoExTarifario: `${normalizedData?.ii?.anoExTarifario || ''}`,
        anoAcordoTarifario: `${normalizedData?.ii?.anoAcordoTarifario || ''}`,
        orgaoEmissorAtoLegalExTarifario: normalizedData?.ii?.orgaoEmissorAtoLegalExTarifario?.id
          ? normalizedData?.ii?.orgaoEmissorAtoLegalExTarifario
          : undefined,
        orgaoEmissorAtoLegalAcordoTarifario: normalizedData?.ii?.orgaoEmissorAtoLegalAcordoTarifario?.id
          ? normalizedData?.ii?.orgaoEmissorAtoLegalAcordoTarifario
          : undefined,
        baseCalculoReal: isValid(normalizedData?.ii?.baseCalculoReal)
          ? unnormalizeNumeral(normalizedData?.ii?.baseCalculoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquota: isValid(normalizedData?.ii?.aliquota)
          ? unnormalizeNumeral(normalizedData?.ii?.aliquota, formatBrazilianNumericDecimal(2))
          : undefined,
        aliquotaAcordo: isValid(normalizedData?.ii?.aliquotaAcordo)
          ? unnormalizeNumeral(normalizedData?.ii?.aliquotaAcordo, formatBrazilianNumericDecimal(2))
          : undefined,
        percentualReducao: isValid(normalizedData?.ii?.percentualReducao)
          ? unnormalizeNumeral(normalizedData?.ii?.percentualReducao, formatBrazilianNumericDecimal(7))
          : undefined,
        aliquotaReduzida: isValid(normalizedData?.ii?.aliquotaReduzida)
          ? unnormalizeNumeral(normalizedData?.ii?.aliquotaReduzida, formatBrazilianNumericDecimal(7))
          : undefined,
        coeficienteReducao: isValid(normalizedData?.ii?.coeficienteReducao)
          ? unnormalizeNumeral(normalizedData?.ii?.coeficienteReducao, formatBrazilianNumericDecimal(7))
          : undefined,
        valorDolar: isValid(normalizedData?.ii?.valorDolar)
          ? unnormalizeNumeral(normalizedData?.ii?.valorDolar, formatBrazilianNumericDecimal(2))
          : undefined,
        valorReal: isValid(normalizedData?.ii?.valorReal)
          ? unnormalizeNumeral(normalizedData?.ii?.valorReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorDevidoReal: isValid(normalizedData?.ii?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.ii?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.ii?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.ii?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
        recolherPermanencia: isValid(normalizedData?.ii?.recolherPermanencia)
          ? unnormalizeNumeral(normalizedData?.ii?.recolherPermanencia, formatBrazilianNumericDecimal(2))
          : undefined,
        acordoAladi: normalizedData?.ii?.acordoAladi ? normalizedData.ii.acordoAladi : undefined,
      },
      taxaSiscomex: {
        ...normalizedData?.taxaSiscomex,
        valorReal: isValid(normalizedData?.taxaSiscomex?.valorReal)
          ? unnormalizeNumeral(normalizedData?.taxaSiscomex?.valorReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      multaLi: {
        ...normalizedData?.multaLi,
        valorDevidoReal: isValid(normalizedData?.multaLi?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.multaLi?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.multaLi?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.multaLi?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
      valorDespesaAcessoriaReal: isValid(normalizedData?.valorDespesaAcessoriaReal)
        ? unnormalizeNumeral(normalizedData?.valorDespesaAcessoriaReal, formatBrazilianNumericDecimal(2))
        : undefined,
      afrmm: {
        ...normalizedData?.afrmm,
        valorDevidoReal: isValid(normalizedData?.afrmm?.valorDevidoReal)
          ? unnormalizeNumeral(normalizedData?.afrmm?.valorDevidoReal, formatBrazilianNumericDecimal(2))
          : undefined,
        valorRecolherReal: isValid(normalizedData?.afrmm?.valorRecolherReal)
          ? unnormalizeNumeral(normalizedData?.afrmm?.valorRecolherReal, formatBrazilianNumericDecimal(2))
          : undefined,
      },
    };

    // unnormalize atributos fundamento legal duimp

    if (
      normalizedData?.atributosSimplesFundamentoLegalDuimp &&
      Array.isArray(normalizedData?.atributosSimplesFundamentoLegalDuimp)
    ) {
      // inserir atributos simples

      adicaoDuimp.atributosFundamentoLegalDuimp = { atributosPreenchidos: { atributos: {} } };

      normalizedData.atributosSimplesFundamentoLegalDuimp.forEach((el) => {
        adicaoDuimp.atributosFundamentoLegalDuimp.atributosPreenchidos.atributos[`${el.atributo}`] = {
          valor: el.valor,
        };
      });
    }

    return adicaoDuimp;
  }, []);

  return {
    handleConsultarClick,
    handleImprimirClick,
    handleFormDialogImpressaoStatus,
    handleGerarXMLDiagnosticoById,
    handleGerarXMLRegistroById,
    handleDiagnosticoCrawlerClick,
    formDialogImpressaoSubmittingFeedback,
    isFormDialogImpressaoOpen,
    handleCloseFormDialogImpressao,
    formDialogImpressaoContent,
    handleImprimirByIdSubmit,
    reportRelatedModel,
    canGenerateDIDiag,
    canGenerateTransmissaoDiagnosticoDI,
    diagnosticoDiPopUpConfig,
    canGenerateDIReg,
    canGenerateTransmissaoRegistroDI,
    handleTransmissaoDuimp,
    handleDiagnosticarDuimp,
    exibirModalTransmissaoDuimp,
    setExibirModalTransmissaoDuimp,
    modelDuimpSelecionada,
    setModelDuimpSelecionada,
    registrarDuimp,
    handleCalcularClick,
    formDialogCalculoStatus,
    handleFormDialogCalculoStatus,
    operationOfConferirById,
    handleConfigureConferirByIdClick,
    aliquotasDiferemPopUpConfig,
    handleConfigureLiberarByIdClick,
    handleConfigureRetransmitirByIdClick,
    reabriDiPopUpConfig,
    operationOfLiberarById,
    operationOfRetransmitirById,
    handleDadosRegistroSiscomexClick,
    formDialogDadosRegistroSiscomexStatus,
    handleFormDialogDadosRegistroSiscomexStatus,
    handleDuplicarClick,
    formDialogDuplicacaoStatus,
    handleFormDialogDuplicacaoStatus,
    handleDuplicarByProcessoSubmit,
    handleCloseFormDialogDuplicacao,
    isFormDialogDuplicacaoOpen,
    formDialogDuplicacaoContent,
    duplicataRelatedModel,
    filterAvailableFollowUp,
    handleIncluirMultaClick,
    formDialogIncluirMultaStatus,
    handleFormDialogIncluirMultaStatus,
    isFormDialogIncluirMultaOpen,
    multaRelatedModel,
    handleIncluirMultaSubmit,
    handleCloseFormDialogIncluirMulta,
    fetchAll,
    handleEmissaoIcmsClick,
    formDialogEmissaoIcmsStatus,
    handleFormDialogEmissaoIcmsStatus,
    isFormDialogEmissaoIcmsOpen,
    formDialogEmissaoIcmsContent,
    handleEmissaoIcmsSubmit,
    handleCloseFormDialogEmissaoIcms,
    handleCloseEmissaoIcmsOperation,
    operationOfEmissaoIcms,
    operationOfActivateOrInactivateById,
    handleConfigureActivateOrInactivateByIdClick,
    formDialogCalculoContent,
    handleBulkCalculateSubmit,
    isFormDialogCalculoOpen,
    handleCloseFormDialogCalculo,
    handleSuccessBulkOperation,
    handleCalcularByDataSubmit,
    isCalculate,
    handleAtualizarDadosCarga,
    handleObterChavesDuimp,
    handleRetificarDuimp,
    handleRecuperarStatusDuimp,
    calculoRelatedModel,
    handleReabrirClick,
    emissaoIcmsRelatedModel,
    isCalcularAcrescimosDeducoesAlertOpen,
    handleCalcularByDataSubmitAlert,
    handleCloseAlertAcrescimosDeducoes,
    fetchFaturaDiDuimpCapaByFaturaId,
    exibirModalRelatorioCalculoDi,
    setExibirModalRelatorioCalculoDi,
    idDiSelecionada,
    setIdDiSelecionada,
    idFaturaSelecionada,
    setIdFaturaSelecionada,
    unnormalize,
    handleDeleteTransmissaoDuimpByClick,
    deleteTransmissaoDuimpPopup,
    exibirModalDespachante,
    setExibirModalDespachante,
    duimpSelecionadaTransmissao,
    setDuimpSelecionadaTransmissao,
    canGenerateRelatorioSap,
    exibirModalTransmissao,
    setExibirModalTransmissao,
    dataDiagnostico,
    setDataDiagnostico,
    processoDiagnostico,
    setProcessoDiagnostico,
    idDiToTransmissao,
    setIdDiToTransmissao,
    isDuimpSelected,
    setIsDuimpSelected,
    faturaSelected,
    setFaturaSelected,
    handleOpenModalTransmissao,
    transmitirDuimpSemTributos,
    showButtonTransmitirSemTributos,
  };
};

export default useDeclaracaoImportacaoActionsMenu;
